import { useEffect, useState, useCallback } from "react";
import { Pencil, Trash2, Link2, Eye, EyeOff, Plus, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, thumb } from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";
import { Field } from "./Field";

const EMPTY = { title: "", slug: "", description: "", image_url: "", camp_ids: [], two_camp_price: "", all_camp_price: "", active: false };
const num = (v) => (v === "" || v == null ? null : Number(v));

function BundleForm({ form, setForm, camps, onSave, onCancel, editing }) {
  const toggle = (id) => setForm({ ...form, camp_ids: form.camp_ids.includes(id) ? form.camp_ids.filter((x) => x !== id) : [...form.camp_ids, id] });
  return (
    <div data-testid="camp-bundle-form" className="p-6 rounded-2xl bg-white border border-stone-200">
      <h3 className="font-serif font-black text-2xl text-[#121212] mb-1">{editing ? "Edit camp week" : "New camp week (multi-camp deal)"}</h3>
      <p className="text-sm text-stone-500 mb-4">Parents pick any camps in the week. Booking 2 or all camps uses the per-child price below. Siblings get whichever is lower: sibling price or multi-camp price (never both).</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Title" data-testid="bundle-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Field label="URL slug (optional)" data-testid="bundle-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
        <Field label="Price per child — any 2 camps ($)" type="number" data-testid="bundle-two-price" value={form.two_camp_price ?? ""} onChange={(e) => setForm({ ...form, two_camp_price: e.target.value })} />
        <Field label={`Price per child — all ${form.camp_ids.length || ""} camps ($)`} type="number" data-testid="bundle-all-price" value={form.all_camp_price ?? ""} onChange={(e) => setForm({ ...form, all_camp_price: e.target.value })} />
      </div>
      <div className="mt-4"><Field label="Description" type="textarea" data-testid="bundle-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
      <div className="mt-4"><ImageUpload library label="Cover image" testid="bundle-image" value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} /></div>
      <p className="mt-5 text-xs tracking-[0.2em] uppercase text-stone-500 mb-2">Camps in this week</p>
      <div className="grid sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
        {camps.map((c) => (
          <label key={c.id} data-testid={`bundle-camp-${c.id}`} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer ${form.camp_ids.includes(c.id) ? "border-[#121212] bg-[#C6FF33]/40" : "border-stone-200"}`}>
            <input type="checkbox" checked={form.camp_ids.includes(c.id)} onChange={() => toggle(c.id)} className="accent-[#121212]" />
            <span className="text-sm"><strong>{c.date}</strong> · {c.title} · ${c.price}{c.active === false ? " · draft" : ""}</span>
          </label>
        ))}
        {!camps.length && <p className="text-sm text-stone-500">Create Kids Camps in the Classes tab first.</p>}
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm"><input type="checkbox" data-testid="bundle-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#121212]" /> Live on the website</label>
      <div className="mt-5 flex gap-3">
        <button data-testid="bundle-save-btn" onClick={onSave} className="px-6 py-3 rounded-full bg-[#121212] text-white text-sm">{editing ? "Save changes" : "Create camp week"}</button>
        {editing && <button data-testid="bundle-cancel-btn" onClick={onCancel} className="px-6 py-3 rounded-full border border-stone-300 text-sm">Cancel</button>}
      </div>
    </div>
  );
}

function CopyPanel({ b, onDone, onClose }) {
  const [name, setName] = useState("");
  const [word, setWord] = useState((b.title || "").split(" ")[0]);
  const [dates, setDates] = useState(b.camps.map(() => ""));
  const [busy, setBusy] = useState(false);
  const preview = (t) => (word && name ? t.split(word).join(name) : t);
  const run = async () => {
    if (!name.trim()) return toast.error("Enter a name for the new week (e.g. Christmas).");
    if (dates.some((d) => !d)) return toast.error("Pick a date for every camp.");
    setBusy(true);
    try {
      await api.post(`/admin/camp-bundles/${b.id}/copy`, { week_name: name.trim(), replace_word: word.trim(), dates });
      toast.success("Camp week copied as drafts — update themes & photos in Classes, then Publish week.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div data-testid={`bundle-copy-panel-${b.id}`} className="mt-3 p-4 rounded-xl bg-[#F5EFE6] border border-stone-200">
      <p className="font-bold text-sm text-[#121212]">Copy this week for another break</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <Field label="Replace this word" data-testid="copy-replace-word" value={word} onChange={(e) => setWord(e.target.value)} />
        <Field label="With (new week name)" data-testid="copy-week-name" placeholder="Christmas" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="mt-3 space-y-2">
        {b.camps.map((c, i) => (
          <div key={c.id} className="grid sm:grid-cols-[1fr_180px] gap-2 items-center">
            <span className="text-sm text-stone-700">{preview(c.title)} <span className="text-stone-400">(was {c.date})</span></span>
            <input type="date" data-testid={`copy-date-${i}`} value={dates[i]} onChange={(e) => setDates(dates.map((d, j) => (j === i ? e.target.value : d)))}
              className="px-3 py-2 rounded-lg border border-stone-200 focus:border-[#121212] focus:outline-none" />
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-stone-500">New week: <strong>{preview(b.title)}</strong>. Everything is saved as drafts with the same prices and photos.</p>
      <div className="mt-3 flex gap-2">
        <button data-testid="copy-confirm-btn" onClick={run} disabled={busy} className="px-5 py-2 rounded-full bg-[#121212] text-white text-sm disabled:opacity-40">{busy ? "Copying…" : "Create copy"}</button>
        <button data-testid="copy-cancel-btn" onClick={onClose} className="px-5 py-2 rounded-full border border-stone-300 text-sm">Cancel</button>
      </div>
    </div>
  );
}

function BundleRow({ b, onEdit, onDelete, onPublish, onCopied }) {
  const [copying, setCopying] = useState(false);
  const drafts = b.camps.filter((c) => c.active === false).length;
  const url = `${window.location.origin}/camps/${b.slug}`;
  return (
    <div className="p-4 rounded-2xl bg-white border border-stone-200">
    <div data-testid={`bundle-row-${b.id}`} className="flex flex-col md:flex-row gap-4 md:items-center">
      {b.image_url && <img src={thumb(b.image_url, 200)} alt="" className="w-20 h-20 rounded-lg object-cover" />}
      <div className="flex-1 min-w-0">
        <p className="font-serif font-black text-xl text-[#121212]">{b.title} <span className={`ml-2 align-middle text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${b.active ? "bg-[#C6FF33]" : "bg-stone-200"}`}>{b.active ? "Live" : "Draft"}</span></p>
        <p className="text-sm text-stone-600">{b.camps.length} camps · 2 camps ${b.two_camp_price ?? "—"}/child · all ${b.all_camp_price ?? "—"}/child</p>
        <p className="text-xs text-stone-500 truncate">{b.camps.map((c) => `${c.date} ${c.title}`).join(" • ")}</p>
        {drafts > 0 && <p data-testid={`bundle-draft-warning-${b.id}`} className="text-xs text-amber-700 mt-1">{drafts} camp{drafts > 1 ? "s are" : " is"} still a draft — use "Publish week" to make everything bookable.</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        {(!b.active || drafts > 0) && <button data-testid={`bundle-publish-${b.id}`} onClick={() => onPublish(b)} className="px-4 py-2 rounded-full bg-[#C6FF33] border border-[#121212] text-xs font-bold inline-flex items-center gap-1"><Eye size={14} /> Publish week</button>}
        {b.active && <button data-testid={`bundle-unpublish-${b.id}`} onClick={() => onPublish(b, false)} className="px-3 py-2 rounded-full border border-stone-300 text-xs inline-flex items-center gap-1"><EyeOff size={14} /> Hide</button>}
        <button data-testid={`bundle-copy-${b.id}`} onClick={() => { navigator.clipboard?.writeText(url); toast.success("Camp week link copied"); }} className="p-2 rounded-full border border-stone-300"><Link2 size={15} /></button>
        <button data-testid={`bundle-copyweek-${b.id}`} onClick={() => setCopying(!copying)} className="px-3 py-2 rounded-full border border-stone-300 text-xs inline-flex items-center gap-1"><Copy size={14} /> Copy week</button>
        <button data-testid={`bundle-edit-${b.id}`} onClick={() => onEdit(b)} className="p-2 rounded-full border border-stone-300"><Pencil size={15} /></button>
        <button data-testid={`bundle-delete-${b.id}`} onClick={() => onDelete(b)} className="p-2 rounded-full border border-stone-300 text-red-500"><Trash2 size={15} /></button>
      </div>
    </div>
    {copying && <CopyPanel b={b} onClose={() => setCopying(false)} onDone={() => { setCopying(false); onCopied(); }} />}
    </div>
  );
}

export default function AdminCampBundles() {
  const [bundles, setBundles] = useState([]);
  const [camps, setCamps] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    const [b, c] = await Promise.all([api.get("/admin/camp-bundles"), api.get("/classes?include_inactive=true")]);
    setBundles(b.data);
    setCamps(c.data.filter((x) => x.category === "Kids Camps" && !x.private).sort((a, z) => a.date.localeCompare(z.date)));
  }, []);
  useEffect(() => { load().catch(() => toast.error("Couldn't load camp weeks")); }, [load]);

  const payload = (f) => ({ ...f, two_camp_price: num(f.two_camp_price), all_camp_price: num(f.all_camp_price) });
  const save = async () => {
    if (!form.title.trim() || form.camp_ids.length < 2) return toast.error("Add a title and pick at least 2 camps.");
    try {
      if (editId) await api.put(`/admin/camp-bundles/${editId}`, payload(form));
      else await api.post("/admin/camp-bundles", payload(form));
      toast.success("Camp week saved");
      setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const edit = (b) => { setEditId(b.id); setForm({ ...EMPTY, ...b, camp_ids: b.camp_ids || [] }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (b) => { if (!window.confirm(`Delete "${b.title}"? The camps themselves stay.`)) return; await api.delete(`/admin/camp-bundles/${b.id}`); load(); };
  const publish = async (b, on = true) => {
    try {
      if (on) await Promise.all(b.camps.filter((c) => c.active === false).map((c) => api.patch(`/classes/${c.id}/active?active=true`)));
      await api.put(`/admin/camp-bundles/${b.id}`, payload({ ...EMPTY, ...b, active: on }));
      toast.success(on ? "Camp week is live!" : "Camp week hidden");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-camp-bundles" className="mt-10 space-y-6">
      <BundleForm form={form} setForm={setForm} camps={camps} onSave={save} editing={!!editId} onCancel={() => { setEditId(null); setForm(EMPTY); }} />
      <div className="space-y-3">
        {bundles.map((b) => <BundleRow key={b.id} b={b} onEdit={edit} onDelete={del} onPublish={publish} onCopied={load} />)}
        {!bundles.length && <p className="text-sm text-stone-500 inline-flex items-center gap-2"><Plus size={14} /> No camp weeks yet.</p>}
      </div>
    </div>
  );
}
